import { useMemo, useState } from "react";
import { reportSections } from "./constants/reportsData";
import ContentCard from "./components/ContentCard";
import MetricsGrid from "./components/MetricsGrid";
import ReportsSidebar from "./components/ReportsSidebar";
import SectionHeader from "./components/SectionHeader";
import { useToast } from "../../Components/Common/Toast/ToastContext";
import type { ActiveFilters, ReportCard } from "./types/reports.types";
import EmptyState from "../../Components/Common/EmptyState/EmptyState";
import ComingSoonIcon from "../../Assets/Img/ComingSoonIcon";
import "./styles/report.scss";

function buildDefaultFilters(): ActiveFilters {
  return reportSections.reduce<ActiveFilters>((acc, section) => {
    if (section.filters?.length) {
      acc[section.id] = section.defaultFilter ?? section.filters[0];
    }
    return acc;
  }, {});
}

export default function ReportsPage() {
  const [activeSection, setActiveSection] = useState<string>(
    reportSections[0].id,
  );
  const [activeFilters, setActiveFilters] =
    useState<ActiveFilters>(buildDefaultFilters);
  const showToast = useToast();

  const currentSection = useMemo(
    () => reportSections.find((s) => s.id === activeSection)!,
    [activeSection],
  );

  return (
    <div className="reportsPage">
      <div className="reportsLayout d-none">
        <ReportsSidebar
          sections={reportSections}
          activeSection={activeSection}
          onSelect={setActiveSection}
        />

        <main className="reportsMain">
          <SectionHeader
            title={currentSection.title}
            subtitle={currentSection.subtitle}
            filters={currentSection.filters}
            activeFilter={activeFilters[currentSection.id]}
            onFilterChange={(nextFilter: string) =>
              setActiveFilters((prev) => ({
                ...prev,
                [currentSection.id]: nextFilter,
              }))
            }
          />

          {currentSection.preface ? (
            <section className="sectionPreface">
              <strong>{currentSection.preface.title}</strong>
              <p>{currentSection.preface.text}</p>
            </section>
          ) : null}

          <MetricsGrid metrics={currentSection.metrics} />

          {currentSection.rows.map((row: ReportCard[], rowIndex: number) => {
            const rowClass =
              row.length === 1
                ? "cardGrid single"
                : row.length === 3
                  ? "cardGrid three"
                  : "cardGrid two";

            return (
              <section
                key={`${currentSection.id}-row-${rowIndex}`}
                className={rowClass}
              >
                {row.map((card) => (
                  <div className="table-responsive" key={card.title}>
                    <ContentCard card={card} onAction={showToast} />
                  </div>
                ))}
              </section>
            );
          })}
        </main>
      </div>
      <EmptyState
        noBorder
        height={"100vh"}
        icon={<ComingSoonIcon />}
        title="Reports Coming Soon"
        subtitle="We're working hard to bring you powerful reporting and analytics. This feature is currently under development and will be available in a future update."
      />
    </div>
  );
}
